// src/pages/PlanPage.jsx
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Wine, Calendar, Clock, Users, Wallet, Leaf, ArrowRight, Check, User, ChevronDown, Search, X } from "lucide-react";
import { TopBar, RoadBadge, ErrorMsg } from "../components/UI";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

const ROADS = [
  { slug: "ar-valley",     label: "Ahr Valley",      country: "Germany",         description: "Pinot Noir country. Compact, dramatic, intimate." },
  { slug: "vienna-venice", label: "Vienna – Venice", country: "Austria · Italy", description: "Cross-border. Riesling to Friulano. 7-day adventure." },
  { slug: "malopolska",    label: "Małopolska",      country: "Poland",          description: "Eastern Europe's hidden wine frontier." },
];

const BUDGET_OPTIONS = [
  { value: 1, label: "Budget",   hint: "Local spots, self-catering"  },
  { value: 2, label: "Moderate", hint: "Mix of experiences"          },
  { value: 3, label: "Premium",  hint: "Estate dinners, guided tours" },
  { value: 4, label: "Luxury",   hint: "Private access, sommelier"   },
];

const ROAD_BADGE = {
  "ar-valley":     { bg: "rgba(107,39,55,0.1)",   color: "#886070", border: "rgba(107,39,55,0.2)"  },
  "vienna-venice": { bg: "rgba(122,106,85,0.1)",  color: "#7A6A55", border: "rgba(122,106,85,0.2)" },
  "malopolska":    { bg: "rgba(74,112,82,0.1)",   color: "#4A7052", border: "rgba(74,112,82,0.2)"  },
};


// Segment lists for the "Wineries per segment" stepper card. Tour mode's
// 6 segments are each INDEPENDENTLY settable (not mirrored from the
// outbound leg — see model.mod's RoundTrip comment: K[k] and K[m+1-k] can
// genuinely differ), so this is a real 6-row list, not the 3-row list
// rendered twice. max per segment mirrors its physical counterpart's cap
// (same real candidate pool on both directions of a mirror pair).
const ROUTE_SEGMENTS = [
  { idx: 0, from: "Vienna",  to: "Maribor", max: 8 },
  { idx: 1, from: "Maribor", to: "Gorizia", max: 6 },
  { idx: 2, from: "Gorizia", to: "Venice",  max: 6 },
];
const TOUR_SEGMENTS = [
  { idx: 0, from: "Vienna",  to: "Maribor", max: 8 },
  { idx: 1, from: "Maribor", to: "Gorizia", max: 6 },
  { idx: 2, from: "Gorizia", to: "Venice",  max: 6 },
  { idx: 3, from: "Venice",  to: "Gorizia", max: 6 },
  { idx: 4, from: "Gorizia", to: "Maribor", max: 6 },
  { idx: 5, from: "Maribor", to: "Vienna",  max: 8 },
];

// Mirrors backend's reversal formula (gathering-agent's _reverse_base_segments
// / mcp-geo's _reverse_tij_segments: new segment i is the depot-endpoint-swapped
// mirror of old segment (n-1-i), interior legs untouched) so the "wineries per
// segment" stepper's from/to labels and max caps always match what start_point
// will actually cause the backend to build. Also doubles as the round_trip
// mirror formula itself (TOUR_SEGMENTS' rows 3-5 already equal
// reverseSegments(ROUTE_SEGMENTS) with idx reassigned) — same underlying move.
function reverseSegments(segs) {
  const n = segs.length;
  return segs.map((_, i) => {
    const src = segs[n - 1 - i];
    return { idx: i, from: src.to, to: src.from, max: src.max };
  });
}

const OBJECTIVES = [
  { value: "min_time",    label: "Fastest route",    hint: "Minimize total travel time",       icon: "⏱" },
  { value: "max_rating",  label: "Highest rated",    hint: "Best reviewed wineries",            icon: "⭐" },
  { value: "max_feedback",label: "Most reviewed",    hint: "Most popular wineries",             icon: "💬" },
];

// Departure date+time picked here doesn't feed the solver at all — it's a
// booking-time input only, read later by TourResultsPage's "Book this tour"
// stepper to compute earliest-feasible-arrival per stop via mcp-booking's
// live per-click engine. Defaults to tomorrow at 8:00 AM.
function defaultDepartureAt() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T08:00`;
}

// checked = every wine in the group is selected, indeterminate = some are.
// React has no `indeterminate` JSX prop — it's a DOM-only property — so it
// has to be set imperatively via a ref. Restored (was dropped when the
// wine-filter coverage slider was added — see facetState/toggleFacetGroup
// below for why the tabs are genuinely cross-connected again).
function TriStateCheckbox({ state, onChange }) {
  const ref = React.useRef(null);
  React.useEffect(() => { if (ref.current) ref.current.indeterminate = state === "some"; }, [state]);
  return (
    <input ref={ref} type="checkbox" checked={state === "all"} onChange={onChange}
      style={{ accentColor: "#8B1A2F", width: 14, height: 14, flexShrink: 0 }} />
  );
}

export default function PlanPage() {
  const navigate = useNavigate();
  const { user } = useAuth("traveler");
  const isTraveler = user?.role === "traveler";
  const EMPTY_WINE_FILTER = { name_ids: [], color_ids: [], sugar_ids: [], co2_ids: [], min_match_pct: 0, time_deviation_hours: 1 };
  // round_trip/unique_wineries/reoccur_count only ever apply to
  // vienna-venice (the only road with a "Tour" mode — see model.mod's
  // RoundTrip param and mcp-solver's round_trip passthrough). unique_wineries
  // is the traveler-facing framing of reoccur_count=0 (see model.mod's
  // cond_reoccur_cap comment); reoccur_count itself only matters when
  // unique_wineries is false, and is clamped to [0, sum of the 3 outbound
  // k_values] — see setRoundTrip/maxReoccur below.
  // start_point only ever applies to vienna-venice (the only road with more
  // than one depot at either end worth choosing between — see backend's
  // gathering-agent build_vgtoVR/_reverse_base_segments and mcp-geo
  // build_tij/_reverse_tij_segments, both of which match this string
  // case-insensitively against the road's own last waypoint name). "vienna"
  // is the road's natural forward direction and needs no reversal; "venice"
  // reverses the base segments before any round_trip mirroring is applied.
  const [form, setForm] = useState({ road_slug: "", days: 1, user_email: "", notify_on_completion: false, party_size: 2, budget_tier: 2, organic_only: false, objective: "min_time", epsilon_hours: 1, k_values: [1, 1, 1], round_trip: false, unique_wineries: true, reoccur_count: 0, wine_filter: EMPTY_WINE_FILTER, departure_at: defaultDepartureAt(), start_point: "vienna" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = useCallback((key, val) => setForm(f => ({ ...f, [key]: val })), []);

  // Switching Route<->Tour resets k_values to that mode's own length
  // (3 vs 6) — a stale 3-length array in Tour mode would silently fall
  // back to mcp-solver's K=[3]*6 default (see mcp-solver/handler.py's
  // `if k_values and len(k_values) >= m` check), which is a correct but
  // surprising 1-winery-per-segment default the traveler never chose.
  // Resets unique_wineries/reoccur_count too, since a reoccur_count
  // carried over from a previous Tour-mode session could exceed the new
  // k_values' own bound (see maxReoccur below).
  const setRoundTrip = useCallback((roundTrip) => {
    setForm(f => ({
      ...f,
      round_trip: roundTrip,
      k_values: roundTrip ? [1, 1, 1, 1, 1, 1] : [1, 1, 1],
      unique_wineries: true,
      reoccur_count: 0,
    }));
  }, []);
  // Upper bound per the agreed design: a winery can only reoccur between a
  // segment and its direct mirror (never unrelated segments), and the cap
  // is a SINGLE global bound across the whole tour, not per-pair — see
  // model.mod's cond_reoccur_cap comment. Computed from the 3 OUTBOUND
  // k_values (indices 0-2) since form.k_values[i] already equals K[i+1]-2
  // (see mcp-solver's `K[k]=k_values[k]+2`).
  const maxReoccur = form.k_values.slice(0, 3).reduce((a, b) => a + b, 0);

  // Wines available on the selected road, for the wine-filter dropdown.
  // search_wines() already returns each wine's full classificator set
  // (colour/sugar/co2 id + translated name), so the four tabs below are
  // just different groupings of this one fetch — no extra API calls.
  // Refetched whenever the road changes; selection resets too since a
  // wine checked for one road has no meaning on another.
  const [wineRows, setWineRows] = useState([]);
  const [wineOptionsLoading, setWineOptionsLoading] = useState(false);
  const [wineDropdownOpen, setWineDropdownOpen] = useState(false); // closed by default
  const [wineTab, setWineTab] = useState("name"); // name | color | sugar | co2
  const [wineNameSearch, setWineNameSearch] = useState(""); // Name-tab-only filter box
  useEffect(() => {
    if (!form.road_slug) { setWineRows([]); return; }
    setWineOptionsLoading(true);
    api.searchWines(form.road_slug)
      .then(rows => {
        const byId = new Map();
        for (const r of rows) if (!byId.has(r.wine_id)) byId.set(r.wine_id, r);
        setWineRows([...byId.values()]);
      })
      .catch(() => setWineRows([]))
      .finally(() => setWineOptionsLoading(false));
    setForm(f => ({ ...f, wine_filter: EMPTY_WINE_FILTER, start_point: "vienna" }));
    setWineDropdownOpen(false);
    setWineTab("name");
    setWineNameSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.road_slug]);

  // Every tab (Name / Color / Sugar / CO2) reads and writes the SAME flat
  // set of wine ids — wine_filter.name_ids — which is exactly the
  // "target_wine_ids" set mcp-winery/handler.py::_resolve_wine_filter and
  // gathering-agent's coverage curation already operate over server-side
  // (see that function's own docstring: coverage is measured against the
  // flat resolved set of wine names, NOT a count of "how many tabs' worth
  // of criteria" were checked). color_ids/sugar_ids/co2_ids are kept in
  // EMPTY_WINE_FILTER only so the wire shape stays backward-compatible
  // with a backend that still reads those keys (always sent empty) — the
  // client never writes to them anymore.
  //
  // A facet-value row (e.g. "Red" under Color) represents a *group* of
  // wine ids. Its checkbox is tri-state — checked only if every wine in
  // the group is currently in name_ids, indeterminate if some are — and
  // toggling it adds/removes the whole group from name_ids. Because every
  // tab reads/writes that same array, checking a value on one tab is
  // immediately reflected (as checked, or indeterminate where it partially
  // overlaps) on every other tab — that's the actual "tabs are
  // inter-connected" behavior (previously true, then silently dropped when
  // the coverage-threshold slider was added; restored here).
  function facetState(ids) {
    const selected = ids.filter(id => form.wine_filter.name_ids.includes(id)).length;
    if (selected === 0) return "none";
    if (selected === ids.length) return "all";
    return "some";
  }

  function toggleFacetGroup(ids) {
    const state = facetState(ids);
    setForm(f => {
      const nextIds = new Set(f.wine_filter.name_ids);
      if (state === "all") ids.forEach(id => nextIds.delete(id));
      else ids.forEach(id => nextIds.add(id));
      return { ...f, wine_filter: { ...f.wine_filter, name_ids: [...nextIds] } };
    });
  }

  // Name tab: toggling a single wine is just a one-element facet group.
  function toggleWine(id) {
    toggleFacetGroup([id]);
  }

  function classificatorLabel(translations) {
    if (!translations) return "Unclassified";
    return translations.en || Object.values(translations)[0] || "Unclassified";
  }

  function buildFacet(idKey, nameKey, extra) {
    const groups = new Map();
    for (const r of wineRows) {
      const key = r[idKey];
      if (key == null) continue;
      if (!groups.has(key)) {
        groups.set(key, { id: key, label: classificatorLabel(r[nameKey]), wineIds: [], ...(extra ? extra(r) : {}) });
      }
      groups.get(key).wineIds.push(r.wine_id);
    }
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  const wineOptions = React.useMemo(
    () => [...wineRows].sort((a, b) => a.name.localeCompare(b.name)).map(r => ({ id: r.wine_id, name: r.name })),
    [wineRows]
  );
  // Name-tab-only substring filter, case-insensitive. Purely a display
  // filter over wineOptions — doesn't touch form.wine_filter, so a wine
  // stays checked (and still counts toward totalCriteria) even while it's
  // scrolled out of view under an active search term.
  const visibleWineOptions = React.useMemo(() => {
    const q = wineNameSearch.trim().toLowerCase();
    if (!q) return wineOptions;
    return wineOptions.filter(w => w.name.toLowerCase().includes(q));
  }, [wineOptions, wineNameSearch]);
  const colorOptions = React.useMemo(() => buildFacet("colour_id", "colour_name", r => ({ hex: r.hex_hint })), [wineRows]);
  const sugarOptions = React.useMemo(() => buildFacet("sugar_type_id", "sugar_type_name"), [wineRows]);
  const co2Options   = React.useMemo(() => buildFacet("co2_type_id", "co2_type_name"), [wineRows]);

  const WINE_TABS = [
    { key: "name",  label: "Name" },
    { key: "color", label: "Color" },
    { key: "sugar", label: "Sugar" },
    { key: "co2",   label: "CO₂" },
  ];
  const activeFacetOptions = { color: colorOptions, sugar: sugarOptions, co2: co2Options }[wineTab];
  // Single flat set now (see facetState/toggleFacetGroup above) — this is
  // literally the same count the backend measures coverage against, not a
  // count of "how many tabs' worth of criteria" were touched.
  const totalCriteria = form.wine_filter.name_ids.length;

  // Registered travelers: no email field on this form (identity + notify
  // preference come from the account, verified server-side by the Bearer
  // token) — prefill from predefined_plan_criteria so returning users don't
  // have to re-pick their usual defaults.
  useEffect(() => {
    if (!isTraveler) return;
    setForm(f => ({
      ...f,
      notify_on_completion: user.notify_on_plan_completion !== false,
      ...(user.predefined_plan_criteria || {}),
    }));
  }, [isTraveler, user]);

  async function handleStart(e) {
    e.preventDefault();
    if (!form.road_slug) { setError("Pick a wine road to continue."); return; }
    setError(""); setLoading(true);
    try {
      // departure_at now also feeds the solver's optional time-window
      // feasibility (opening hours, see model.mod's min_time_tw problem) —
      // no longer excluded from planForm. Still separately pulled out here
      // and carried via navigation state + localStorage too, since
      // TourResultsPage's live booking flow (get_plan_stop_slots) needs it
      // client-side independent of whatever the plan payload does with it.
      const { departure_at } = form;
      const planForm = {
        ...form,
        epsilon_values: form.objective !== "min_time" ? [form.epsilon_hours * 60] : [],
        k_values: form.k_values,
        // round_trip only ever means anything on vienna-venice — forced
        // false here regardless of stale form state from a previous road
        // selection (see setRoundTrip's comment on why switching roads
        // doesn't itself reset this). reoccur_count only matters when
        // round_trip is set and unique_wineries is off — the traveler-
        // facing "unique wineries" switch is just reoccur_count=0 at the
        // app layer (see model.mod's cond_reoccur_cap comment).
        round_trip: form.road_slug === "vienna-venice" ? form.round_trip : false,
        reoccur_count: (form.road_slug === "vienna-venice" && form.round_trip && !form.unique_wineries)
          ? form.reoccur_count : 0,
        // Same vienna-venice gating as round_trip above — start_point only
        // means anything there (see gathering-agent's build_vgtoVR docstring).
        start_point: form.road_slug === "vienna-venice" ? form.start_point : "vienna",
        user_email: isTraveler ? undefined : (form.user_email || undefined),
        notify_on_completion: form.notify_on_completion,
      };
      const res = await api.postPlan(planForm);
      // Carried to TourResultsPage via navigation state (fast path) and
      // localStorage keyed by plan_id (survives a refresh/direct link,
      // since res.plan_id is stable and the results page may be reloaded
      // independently of this navigation).
      try { localStorage.setItem(`wr_departure_${res.plan_id}`, departure_at); } catch {}
      navigate(`/results?plan=${res.plan_id}`, { state: { departure_at } });
    } catch (err) {
      setError(err.message || "Failed to generate tour.");
      setLoading(false);
    }
  }

  const S = {
    page: { minHeight: "100vh", paddingBottom: 80, background: "#F2F5EC" },
    header: { padding: "20px 16px 6px" },
    eyebrow: { fontSize: 10, fontWeight: 600, color: "#2E3D48", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 },
    title: { fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "#1E2C14", margin: 0 },
    roads: { padding: "14px 16px 0", display: "flex", flexDirection: "column", gap: 8 },
    form: { padding: "20px 16px 0", display: "flex", flexDirection: "column", gap: 20 },
    label: { fontSize: 10, fontWeight: 600, color: "#2E3D48", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 },
  };

  return (
    <div style={S.page}>
      <TopBar title="Plan a tour" right={
        isTraveler ? (
          <Link to="/account" style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"#886070",textDecoration:"none"}}>
            <User size={13} /> {user.full_name || user.email}
          </Link>
        ) : (
          <Link to="/tourist/login" state={{ from: "/plan" }} style={{
            fontSize:12,fontWeight:600,color:"#6B2737",textDecoration:"none",
            padding:"5px 10px",borderRadius:6,border:"1px solid rgba(107,39,55,0.3)",
          }}>Sign in</Link>
        )
      } />
      <div style={S.header}>
        <p style={S.eyebrow}>Wine roads</p>
        <h2 style={S.title}>Where do you want to go?</h2>
      </div>

      {isTraveler ? null : (
        <div style={{padding:"0 16px 14px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:10,fontWeight:600,color:"#6A7E58",textTransform:"uppercase",letterSpacing:"0.08em"}}>Your email</span>
            <span style={{fontSize:11,color:"#8BAD3F"}}>✉ required for booking</span>
          </div>
          <input type="email" placeholder="your@email.com" value={form.user_email}
            onChange={e => set("user_email", e.target.value)}
            style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid #C4D0A8",background:"#EDF1E4",color:"#2C3820",fontSize:13,boxSizing:"border-box"}}
          />
          {form.user_email && (
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                <span style={{fontSize:12,color:"#5A6E48"}}>Notify me on tour ready</span>
                <input type="checkbox" checked={form.notify_on_completion}
                  onChange={e => set("notify_on_completion", e.target.checked)}
                  style={{accentColor:"#8B1A2F",width:15,height:15,flexShrink:0}}
                />
              </label>
            </div>
          )}
          <p style={{fontSize:11,color:"#8A9E78",marginTop:8}}>
            <Link to="/tourist/login" state={{ from: "/plan" }} style={{color:"#8A9E78"}}>Sign in</Link> to save this tour and skip re-entering your email next time.
          </p>
        </div>
      )}

      <div style={S.roads}>
        {ROADS.map(road => {
          const sel = form.road_slug === road.slug;
          const bs = ROAD_BADGE[road.slug];
          return (
            <React.Fragment key={road.slug}>
            <button onClick={() => set("road_slug", road.slug)} style={{
              width: "100%", textAlign: "left",
              background: sel ? "#E2E8D8" : "#EDF1E4",
              border: sel ? "1px solid #303030" : "1px solid #222222",
              borderRadius: 8, padding: "14px 16px", cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { if (!sel) { e.currentTarget.style.background="#EAEFE2"; e.currentTarget.style.borderColor="#B8C8A0"; }}}
            onMouseLeave={e => { if (!sel) { e.currentTarget.style.background="#EDF1E4"; e.currentTarget.style.borderColor="#C8D4B0"; }}}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: 15, color: sel ? "#1E2C14" : "#4A5E38" }}>
                      {road.label}
                    </span>
                    <span className="road-badge" style={{ background: bs.bg, color: bs.color, border: `1px solid ${bs.border}` }}>
                      <Wine size={9} />{road.slug.replace("-", " ").toUpperCase()}
                    </span>
                  </div>
                  <p style={{ fontSize: 11, color: "#6A7E58", margin: "0 0 3px" }}>{road.country}</p>
                  <p style={{ fontSize: 12, color: "#777", margin: 0, lineHeight: 1.45 }}>{road.description}</p>
                </div>
                {sel && (
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%", background: "#6B2737",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 3,
                  }}>
                    <Check size={10} color="white" strokeWidth={2.5} />
                  </div>
                )}
              </div>
            </button>
          {sel && road.slug === "vienna-venice" && (
            <div style={{background:"#F2F5EC",borderLeft:"2px solid #6B2737",borderRight:"1px solid #252525",borderBottom:"1px solid #252525",borderRadius:"0 0 8px 8px",padding:"10px 14px",marginTop:-2}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:9,fontWeight:700,color:"#6B2737",textTransform:"uppercase",letterSpacing:"0.12em"}}>Trip type</span>
              </div>
              <div style={{display:"flex",gap:6,marginBottom:12}}>
                {[
                  {v:false,label:"Route",hint:"One-way, Vienna → Venice"},
                  {v:true, label:"Tour", hint:"Round trip, back to Vienna"},
                ].map(opt=>{
                  const active = form.round_trip === opt.v;
                  return (
                    <button key={String(opt.v)} type="button"
                      onClick={e=>{e.stopPropagation();setRoundTrip(opt.v);}}
                      style={{
                        flex:1,textAlign:"left",padding:"7px 10px",borderRadius:7,cursor:"pointer",
                        background: active ? "#E2E8D8" : "#EDF1E4",
                        border: active ? "1px solid #6B2737" : "1px solid #252525",
                      }}>
                      <div style={{fontSize:12,fontWeight:600,color: active ? "#1E2C14" : "#5A6E48"}}>{opt.label}</div>
                      <div style={{fontSize:10,color:"#8A9E78"}}>{opt.hint}</div>
                    </button>
                  );
                })}
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:9,fontWeight:700,color:"#6B2737",textTransform:"uppercase",letterSpacing:"0.12em"}}>Starting point</span>
              </div>
              <div style={{display:"flex",gap:6,marginBottom:12}}>
                {[
                  {v:"vienna",label:"Vienna",hint: form.round_trip ? "Depart & return to Vienna" : "Vienna → Venice"},
                  {v:"venice",label:"Venice", hint: form.round_trip ? "Depart & return to Venice" : "Venice → Vienna"},
                ].map(opt=>{
                  const active = form.start_point === opt.v;
                  return (
                    <button key={opt.v} type="button"
                      onClick={e=>{e.stopPropagation();set("start_point",opt.v);}}
                      style={{
                        flex:1,textAlign:"left",padding:"7px 10px",borderRadius:7,cursor:"pointer",
                        background: active ? "#E2E8D8" : "#EDF1E4",
                        border: active ? "1px solid #6B2737" : "1px solid #252525",
                      }}>
                      <div style={{fontSize:12,fontWeight:600,color: active ? "#1E2C14" : "#5A6E48"}}>{opt.label}</div>
                      <div style={{fontSize:10,color:"#8A9E78"}}>{opt.hint}</div>
                    </button>
                  );
                })}
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:9,fontWeight:700,color:"#6B2737",textTransform:"uppercase",letterSpacing:"0.12em"}}>Wineries per segment</span>
                <span style={{fontSize:10,color:"#6A7E58"}}>{form.k_values.reduce((a,b)=>a+b,0)} total</span>
              </div>
              <div style={form.round_trip ? {
                  display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "repeat(3, auto)",
                  gridAutoFlow: "column", columnGap: 14, rowGap: 7,
                } : {}}>
                {(() => {
                  // Mirror the backend's own reversal (see reverseSegments'
                  // comment above): when starting from Venice, the base 3
                  // outbound segments are the endpoint-swapped mirror of the
                  // natural Vienna-first order, and round_trip's own 3 return
                  // segments (if shown) mirror THAT already-reversed base —
                  // never the original ROUTE_SEGMENTS — so the labels/caps
                  // shown here always match what gathering-agent/mcp-geo will
                  // actually build for this start_point + round_trip pair.
                  const base = form.start_point === "venice" ? reverseSegments(ROUTE_SEGMENTS) : ROUTE_SEGMENTS;
                  const segments = form.round_trip
                    ? [...base, ...reverseSegments(base)].map((s, i) => ({ ...s, idx: i }))
                    : base;
                  return segments;
                })().map(({idx,from,to,max}, i, arr)=>(
                  <div key={idx} style={{
                    display:"flex",alignItems:"center",gap: form.round_trip ? 5 : 8,
                    marginBottom: form.round_trip ? 0 : (i<arr.length-1?7:0),
                  }}>
                    <span style={{
                      fontSize: form.round_trip ? 10 : 11, color:"#6A7E58",
                      width: form.round_trip ? 96 : 118, flexShrink:0,
                    }}>{from} → {to}</span>
                    <button type="button" onClick={e=>{e.stopPropagation();const k=[...form.k_values];k[idx]=Math.max(1,k[idx]-1);set("k_values",k);}} style={{width: form.round_trip?18:20,height: form.round_trip?18:20,borderRadius:5,border:"1px solid #252525",background:"#EAEFE2",color:"#5A6E48",cursor:"pointer",fontSize: form.round_trip?12:14,lineHeight: form.round_trip?"18px":"20px",textAlign:"center",flexShrink:0}}>−</button>
                    <span style={{width: form.round_trip?16:20,textAlign:"center",fontSize:12,fontWeight:600,color:"#1E2C14"}}>{form.k_values[idx]}</span>
                    <button type="button" onClick={e=>{e.stopPropagation();const k=[...form.k_values];k[idx]=Math.min(max,k[idx]+1);set("k_values",k);}} style={{width: form.round_trip?18:20,height: form.round_trip?18:20,borderRadius:5,border:"1px solid #252525",background:"#EAEFE2",color:"#5A6E48",cursor:"pointer",fontSize: form.round_trip?12:14,lineHeight: form.round_trip?"18px":"20px",textAlign:"center",flexShrink:0}}>+</button>
                  </div>
                ))}
              </div>

              {form.round_trip && (
                <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid #DCE4CC"}}>
                  <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
                    <span style={{fontSize:11,color:"#5A6E48"}}>Unique wineries (no repeats on the way back)</span>
                    <input type="checkbox" checked={form.unique_wineries}
                      onChange={e=>{
                        const checked = e.target.checked;
                        setForm(f => ({ ...f, unique_wineries: checked, reoccur_count: checked ? 0 : f.reoccur_count }));
                      }}
                      style={{accentColor:"#8B1A2F",width:15,height:15,flexShrink:0}}
                    />
                  </label>
                  {!form.unique_wineries && (
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                      <span style={{fontSize:11,color:"#6A7E58",flex:1}}>Allow up to this many repeats (max {maxReoccur})</span>
                      <button type="button" onClick={e=>{e.stopPropagation();set("reoccur_count",Math.max(0,form.reoccur_count-1));}} style={{width:20,height:20,borderRadius:5,border:"1px solid #252525",background:"#EAEFE2",color:"#5A6E48",cursor:"pointer",fontSize:14,lineHeight:"20px",textAlign:"center",flexShrink:0}}>−</button>
                      <span style={{width:20,textAlign:"center",fontSize:12,fontWeight:600,color:"#1E2C14"}}>{form.reoccur_count}</span>
                      <button type="button" onClick={e=>{e.stopPropagation();set("reoccur_count",Math.min(maxReoccur,form.reoccur_count+1));}} style={{width:20,height:20,borderRadius:5,border:"1px solid #252525",background:"#EAEFE2",color:"#5A6E48",cursor:"pointer",fontSize:14,lineHeight:"20px",textAlign:"center",flexShrink:0}}>+</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </React.Fragment>
          );
        })}
      </div>

      {form.road_slug && (wineOptionsLoading || wineOptions.length > 0) && (
        <div style={{ padding: "20px 16px 0" }}>
          <button type="button" onClick={() => setWineDropdownOpen(o => !o)} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
            padding: "11px 14px", borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
            background: totalCriteria > 0 ? "rgba(107,39,55,0.08)" : "#EDF1E4",
            border: totalCriteria > 0 ? "1px solid #6B2737" : "1px solid #222222",
            color: totalCriteria > 0 ? "#6B2737" : "#5A6E48",
          }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              {totalCriteria > 0
                ? `Filter by wine — ${totalCriteria} selected`
                : "Filter by wine"}
            </span>
            <ChevronDown size={15} style={{ transform: wineDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }} />
          </button>

          {wineDropdownOpen && (
            <div style={{ marginTop: 8 }}>
              {wineOptionsLoading ? (
                <p style={{ fontSize: 12, color: "#8A9E78", margin: 0 }}>Loading wines…</p>
              ) : (
                <>
                  {totalCriteria > 0 && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                      <button type="button" onClick={() => set("wine_filter", EMPTY_WINE_FILTER)} style={{
                        fontSize: 11, color: "#886070", background: "none", border: "none", cursor: "pointer", padding: 0,
                      }}>Clear</button>
                    </div>
                  )}

                  {/* Satisfaction-power slider: how much of your selected wine
                      names the tour as a whole (union across every winery it
                      visits — solver-a maximizes this directly via model.mod's
                      max_wine_coverage objective) should cover. This doesn't
                      change what the solver optimizes for (it always maximizes
                      coverage within your time-deviation budget below); it's
                      the bar your achieved route gets checked against on the
                      results page. Plain native range, no CSS mirroring —
                      bigger value (stronger requirement) sits to the right,
                      matching normal slider convention. */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#8A9E78", marginBottom: 4 }}>
                      <span>Weak — any match</span>
                      <span>Strong — most must match</span>
                    </div>
                    <input
                      type="range" min={0} max={100} step={5}
                      value={form.wine_filter.min_match_pct}
                      onChange={e => setForm(f => ({ ...f, wine_filter: { ...f.wine_filter, min_match_pct: Number(e.target.value) } }))}
                      style={{ width: "100%", accentColor: "#6B2737" }}
                    />
                  </div>

                  {/* Time-deviation slider: how much longer than the fastest
                      possible route you're willing to accept in order to
                      better satisfy the wine filter. Solver-a solves the
                      whole time-vs-coverage curve from 0 up to this cap in a
                      single pass (orchestrator's _wine_epsilon_sweep) — the
                      results page can show weaker-deviation alternatives from
                      that same curve without another solve. */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#8A9E78", marginBottom: 4 }}>
                      <span>Time deviation</span>
                      <span>up to +{form.wine_filter.time_deviation_hours}h</span>
                    </div>
                    <input
                      type="range" min={0} max={8} step={1}
                      value={form.wine_filter.time_deviation_hours}
                      onChange={e => setForm(f => ({ ...f, wine_filter: { ...f.wine_filter, time_deviation_hours: Number(e.target.value) } }))}
                      style={{ width: "100%", accentColor: "#6B2737" }}
                    />
                    <p style={{ fontSize: 10.5, color: "#8A9E78", marginTop: 4, marginBottom: 0 }}>
                      Route may take up to {form.wine_filter.time_deviation_hours}h longer than the fastest
                      possible route to include more of your selected wines.
                    </p>
                  </div>

                  {/* All four tabs edit the same shared name_ids array — checking a
                      value on one (a wine on Name, or a whole color/sugar/CO2 group)
                      is immediately reflected (checked, or indeterminate where it
                      partially overlaps) on the others. */}
                  <div style={{ display: "flex", gap: 4, marginBottom: 10, background: "#E8EDE0", padding: 3, borderRadius: 8 }}>
                    {WINE_TABS.map(t => (
                      <button key={t.key} type="button" onClick={() => setWineTab(t.key)} style={{
                        flex: 1, padding: "6px 4px", borderRadius: 6, border: "none", cursor: "pointer",
                        fontSize: 11, fontWeight: 600, transition: "all 0.15s",
                        background: wineTab === t.key ? "#6B2737" : "transparent",
                        color: wineTab === t.key ? "#F5EFE8" : "#5A6E48",
                      }}>{t.label}</button>
                    ))}
                  </div>

                  {wineTab === "name" ? (
                    <div>
                      <div style={{ position: "relative", marginBottom: 8 }}>
                        <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#8A9E78", pointerEvents: "none" }} />
                        <input
                          type="text"
                          value={wineNameSearch}
                          onChange={e => setWineNameSearch(e.target.value)}
                          placeholder="Search wines by name…"
                          style={{
                            width: "100%", padding: "8px 30px", borderRadius: 8,
                            border: "1px solid #C4D0A8", background: "#EDF1E4", color: "#2C3820",
                            fontSize: 12.5, boxSizing: "border-box",
                          }}
                        />
                        {wineNameSearch && (
                          <button type="button" onClick={() => setWineNameSearch("")} style={{
                            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                            background: "none", border: "none", cursor: "pointer", padding: 2,
                            color: "#8A9E78", display: "flex",
                          }}>
                            <X size={13} />
                          </button>
                        )}
                      </div>
                      {visibleWineOptions.length === 0 && (
                        <p style={{ fontSize: 12, color: "#8A9E78", margin: 0 }}>No wines match “{wineNameSearch}”.</p>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                        {visibleWineOptions.map(w => {
                          const checked = form.wine_filter.name_ids.includes(w.id);
                          return (
                            <label key={w.id} style={{
                              display: "flex", alignItems: "center", gap: 7, fontSize: 12,
                              color: checked ? "#1E2C14" : "#5A6E48",
                              background: checked ? "#E2E8D8" : "#EDF1E4",
                              border: checked ? "1px solid #303030" : "1px solid #C4D0A8",
                              borderRadius: 7, padding: "7px 9px", cursor: "pointer",
                            }}>
                              <input type="checkbox" checked={checked} onChange={() => toggleWine(w.id)}
                                style={{ accentColor: "#8B1A2F", width: 14, height: 14, flexShrink: 0 }}
                              />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {activeFacetOptions.length === 0 && (
                        <p style={{ fontSize: 12, color: "#8A9E78", margin: 0 }}>No wines with this attribute on this road.</p>
                      )}
                      {activeFacetOptions.map(opt => {
                        const state = facetState(opt.wineIds);
                        return (
                          <label key={opt.id} style={{
                            display: "flex", alignItems: "center", gap: 8, fontSize: 12,
                            color: state !== "none" ? "#1E2C14" : "#5A6E48",
                            background: state !== "none" ? "#E2E8D8" : "#EDF1E4",
                            border: state !== "none" ? "1px solid #303030" : "1px solid #C4D0A8",
                            borderRadius: 7, padding: "7px 9px", cursor: "pointer",
                          }}>
                            <TriStateCheckbox state={state} onChange={() => toggleFacetGroup(opt.wineIds)} />
                            {wineTab === "color" && (
                              <span style={{
                                width: 11, height: 11, borderRadius: "50%", flexShrink: 0,
                                background: opt.hex || "transparent", border: "1px solid rgba(0,0,0,0.25)",
                              }} />
                            )}
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "capitalize" }}>
                              {opt.label}
                            </span>
                            <span style={{ fontSize: 10, color: "#8A9E78", flexShrink: 0 }}>{opt.wineIds.length}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <p style={{ fontSize: 11, color: "#8A9E78", marginTop: 8, marginBottom: 0 }}>
                    {totalCriteria === 0
                      ? "No wines selected — all wineries on this road are eligible."
                      : form.wine_filter.min_match_pct <= 0
                        ? `Your tour needs at least one winery that carries one of your ${totalCriteria} checked item${totalCriteria === 1 ? "" : "s"}.`
                        : `Your tour's wineries, combined, need to cover at least ${form.wine_filter.min_match_pct}% of your ${totalCriteria} checked item${totalCriteria === 1 ? "" : "s"} — no single winery has to carry them all.`}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleStart} style={S.form}>
        {/* Duration */}
        <div>
          <label style={S.label}><Calendar size={10} />Duration</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[1,2,3,4,5,6,7].map(d => (
              <button key={d} type="button" onClick={() => set("days", d)} style={{
                width: 38, height: 38, borderRadius: 7, fontSize: 13, fontWeight: 500,
                border: form.days === d ? "1px solid #3A2028" : "1px solid #222",
                background: form.days === d ? "#6B2737" : "#EDF1E4",
                color: form.days === d ? "#C48890" : "#777",
                cursor: "pointer", transition: "all 0.15s",
              }}>{d}</button>
            ))}
            <span style={{ alignSelf: "center", fontSize: 11, color: "#2E3D48", marginLeft: 2 }}>days</span>
          </div>
        </div>

        {/* Departure time — booking-only, not sent to the solver. Used later
            by the "Book this tour" stepper to compute per-stop feasibility. */}
        <div>
          <label style={S.label}><Clock size={10} />Departure time</label>
          <input type="datetime-local" value={form.departure_at}
            onChange={e => set("departure_at", e.target.value)}
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #C4D0A8", background: "#EDF1E4", color: "#2C3820", fontSize: 13, boxSizing: "border-box" }}
          />
          <p style={{ fontSize: 11, color: "#6A7E58", marginTop: 6 }}>
            When you'll set off — used to check which tastings you can actually book.
          </p>
        </div>

        {/* Travellers */}
        <div>
          <label style={S.label}><Users size={10} />Travellers</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {[
              { op: "-", fn: () => set("party_size", Math.max(1, form.party_size - 1)) },
              { op: "+", fn: () => set("party_size", Math.min(20, form.party_size + 1)) },
            ].map(({ op, fn }, i) => (
              <button key={op} type="button" onClick={fn} style={{
                width: 34, height: 34, borderRadius: 7,
                background: "#EDF1E4", border: "1px solid #222", color: "#888",
                fontSize: 16, fontWeight: 300, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor="#333"; e.currentTarget.style.color="#2C3820"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor="#C8D4B0"; e.currentTarget.style.color="#888"; }}
              >{op}</button>
            ))}
            <span style={{ width: 32, textAlign: "center", fontWeight: 500, fontSize: 15, color: "#5A6E48" }}>
              {form.party_size}
            </span>
          </div>
        </div>

        {/* Budget */}
        <div>
          <label style={S.label}><Wallet size={10} />Budget</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {BUDGET_OPTIONS.map(opt => {
              const sel = form.budget_tier === opt.value;
              return (
                <button key={opt.value} type="button" onClick={() => set("budget_tier", opt.value)} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "11px 14px", borderRadius: 7,
                  border: sel ? "1px solid #2E1E22" : "1px solid #1E1E1E",
                  background: sel ? "#E2D8DA" : "#EDF1E4",
                  cursor: "pointer", transition: "all 0.15s",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: sel ? "#C48890" : "#5A6E48" }}>{opt.label}</span>
                  <span style={{ fontSize: 11, color: "#6A7E58" }}>{opt.hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Objective */}
        <div>
          <label style={S.label}><span style={{fontSize:10}}>⭐</span> Route priority</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {OBJECTIVES.map(obj => {
              const sel = form.objective === obj.value;
              return (
                <button key={obj.value} type="button" onClick={() => set("objective", obj.value)} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "11px 14px", borderRadius: 7,
                  border: sel ? "1px solid #2E1E22" : "1px solid #1A1A1A",
                  background: sel ? "#E2D8DA" : "#EDF1E4",
                  cursor: "pointer", transition: "all 0.15s",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14 }}>{obj.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: sel ? "#886070" : "#4A5E38" }}>{obj.label}</span>
                  </div>
                  <span style={{ fontSize: 11, color: "#7A8E68" }}>{obj.hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Epsilon */}
        {form.objective !== "min_time" && (
          <div>
            <label style={S.label}><span style={{fontSize:10}}>⏱</span> Extra time — {form.epsilon_hours}h</label>
            <input type="range" min={1} max={8} step={1} value={form.epsilon_hours}
              onChange={e => set("epsilon_hours", parseInt(e.target.value))}
              style={{ width: "100%", accentColor: "#6B2737" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#7A8E68", marginTop: 4 }}>
              {[1,2,3,4,5,6,7,8].map(h => <span key={h}>+{h}h</span>)}
            </div>
            <p style={{ fontSize: 11, color: "#6A7E58", marginTop: 6 }}>
              Extra time vs fastest route for {form.objective === "max_rating" ? "higher rated" : "more reviewed"} wineries
            </p>
          </div>
        )}

        {/* Organic */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#EDF1E4", border: "1px solid #1E1E1E", borderRadius: 7, padding: "11px 14px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Leaf size={14} color="#4A7052" />
            <div>
              <p style={{ fontSize: 13, fontWeight: 500, color: "#5A6E48", margin: "0 0 1px" }}>Organic only</p>
              <p style={{ fontSize: 11, color: "#6A7E58", margin: 0 }}>Show certified organic wineries</p>
            </div>
          </div>
          <button type="button" onClick={() => set("organic_only", !form.organic_only)} style={{
            position: "relative", width: 40, height: 22, borderRadius: 11,
            background: form.organic_only ? "#2E5235" : "#E8EDE0",
            border: `1px solid ${form.organic_only ? "#3A6A42" : "#B8C8A0"}`,
            cursor: "pointer", transition: "all 0.2s",
          }}>
            <span style={{
              position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%",
              background: form.organic_only ? "#6AAA7A" : "#2E3D48",
              transition: "transform 0.2s, background 0.2s",
              transform: form.organic_only ? "translateX(18px)" : "translateX(0)",
              display: "block",
            }} />
          </button>
        </div>

        <ErrorMsg message={error} />



        <button type="submit" disabled={loading || !form.road_slug} className="btn-primary"
          style={{ width: "100%", padding: "13px 20px", fontSize: 14, opacity: !form.road_slug ? 0.35 : 1 }}>
          {loading ? "Starting…" : <><span>Build my tour</span><ArrowRight size={15} /></>}
        </button>

        <p style={{ textAlign: "center", fontSize: 11, color: "#2E3D48", paddingBottom: 8 }}>
          You can refine everything through conversation after
        </p>
      </form>
    </div>
  );
}
